import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { GradedFeedback } from "@/components/storyboard/GradedFeedback";
import { CodeMarkdown } from "@/components/storyboard/code/CodeMarkdown";
import { splitPromptAndFences } from "@/components/storyboard/code/codeUtils";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import QuizCheckNextAction from "@/components/storyboard/quiz/QuizCheckNextAction";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import QuizMultipleChoiceOption from "@/components/storyboard/quiz/QuizMultipleChoiceOption";
import { buildOptionsItem } from "@/components/storyboard/quiz/review/buildReviewItem";
import type { QuizMultipleChoiceEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizMultipleChoiceState {
  currentIndex: number;
  // Selected answers are stored by label, not by index: the backend shuffles
  // the answer order on every request, so indices from a previous session
  // would point at different answers after a reload.
  selectedLabels: string[];
  isSubmitted: boolean;
  isCorrect: boolean | null;
  correctCount: number;
}

export interface QuizMultipleChoiceProps extends HiddenVerdictProps {
  entries: QuizMultipleChoiceEntryData[];
  onValidate: (
    selectedLabels: string[],
    entryIndex: number
  ) => Promise<{ correct: boolean | null; feedback?: string }>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  onFinish?: () => void;
  initialPartial?: QuizMultipleChoiceState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizMultipleChoiceState, index: number) => void;
  /**
   * "code" stacks the prompt, fenced snippet, and answers in the inline card
   * (CODE_MULTIPLE_CHOICE), matching CODE_TRUE_FALSE.
   */
  variant?: "default" | "code";
}

// Validates a persisted partial state against the current entries. Discards
// it when the stored labels no longer exist (legacy index-based partials or
// content that changed since the state was saved) — restoring would silently
// mark the wrong answers.
export const sanitizePartial = (
  partial: QuizMultipleChoiceState | undefined,
  entries: QuizMultipleChoiceEntryData[]
): QuizMultipleChoiceState | null => {
  if (!partial || !Array.isArray(partial.selectedLabels)) return null;
  const entry = (entries ?? [])[partial.currentIndex];
  if (!entry) return null;
  const answers = entry.answers ?? [];
  if (!partial.selectedLabels.every((label) => answers.includes(label))) {
    return null;
  }
  return partial;
};

const questionPartsForLayout = (
  question: string,
  isCodeVariant: boolean
): { prompt: string; remainder: string | null } => {
  if (!isCodeVariant) return { prompt: question, remainder: null };
  return splitPromptAndFences(question);
};

const QuizMultipleChoice = ({
  entries,
  onValidate,
  variant = "default",
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizMultipleChoiceProps) => {
  const [restoredPartial] = useState(() =>
    sanitizePartial(initialPartial, entries)
  );
  const [state, setState] = useState<QuizMultipleChoiceState>(() => {
    if (restoredPartial) return restoredPartial;
    return {
      currentIndex: initialIndex ?? 0,
      selectedLabels: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount: 0,
    };
  });

  const hasInteractedRef = useRef<boolean>(restoredPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { currentIndex, selectedLabels, isSubmitted, isCorrect } = state;

  if (!entries || entries.length === 0) return null;

  const currentEntry = entries[currentIndex];

  if (!currentEntry) return null;

  const isCodeVariant = variant === "code";
  const revealUnselectedCorrect = !isCodeVariant;
  const { prompt: questionPrompt, remainder: questionFences } =
    questionPartsForLayout(currentEntry.question, isCodeVariant);
  const answerKey = currentEntry.correctAnswers ?? [];
  const markedCorrect = answerKey.filter(Boolean).length;
  const correctAnswerCount =
    answerKey.length > 0 ? markedCorrect : (currentEntry.selectCount ?? 1);
  const isSingleChoice = correctAnswerCount === 1;
  const instructionHint = isSingleChoice
    ? "Wähle eine Antwort"
    : `Wähle ${correctAnswerCount} Antworten`;

  const handleAnswerToggle = (answer: string) => {
    if (isSubmitted) return;

    hasInteractedRef.current = true;
    setState((prev) => {
      const isCurrentlySelected = prev.selectedLabels.includes(answer);

      if (isSingleChoice) {
        return { ...prev, selectedLabels: isCurrentlySelected ? [] : [answer] };
      }

      if (isCurrentlySelected) {
        return {
          ...prev,
          selectedLabels: prev.selectedLabels.filter((l) => l !== answer),
        };
      }

      if (prev.selectedLabels.length >= correctAnswerCount) return prev;

      return { ...prev, selectedLabels: [...prev.selectedLabels, answer] };
    });
  };

  const handleSubmit = async () => {
    if (selectedLabels.length === 0) return;

    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => onValidate(selectedLabels, currentIndex),
        onAdvance
      );
      return;
    }

    const result = await onValidate(selectedLabels, currentIndex);
    const correct = result.correct ?? false;
    const correctCount = state.correctCount + (correct ? 1 : 0);

    reviewRef.current[currentIndex] = buildOptionsItem({
      prompt: questionPrompt,
      codeSnippet: questionFences ?? undefined,
      options: (currentEntry.answers ?? []).map((label, i) => ({
        label,
        selected: selectedLabels.includes(label),
        correct: !!(currentEntry.correctAnswers ?? [])[i],
      })),
      isCorrect: correct,
      feedback: currentEntry.userFeedback,
    });

    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      isSubmitted: true,
      isCorrect: correct,
      correctCount,
    }));

    if (currentIndex === entries.length - 1) {
      onComplete?.(
        { correct: correctCount, total: entries.length },
        { items: reviewRef.current }
      );
      if (correct) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        selectedLabels: [],
        isSubmitted: false,
        isCorrect: null,
      }));
    }
  };

  const question = <CodeMarkdown>{questionPrompt}</CodeMarkdown>;

  const renderCodeFences = () => {
    if (!questionFences) return null;
    return (
      <div className="pl-3">
        <CodeMarkdown>{questionFences}</CodeMarkdown>
      </div>
    );
  };

  const renderAnswerChoices = () => (
    <div className="flex w-full flex-col gap-4">
      <TaskInstructionHint>{instructionHint}</TaskInstructionHint>
      <div className="grid grid-cols-1 gap-3">
        {(currentEntry.answers ?? []).map((answer, index) => (
          <QuizMultipleChoiceOption
            key={index}
            answer={answer}
            isSelected={selectedLabels.includes(answer)}
            isAnswerCorrect={!!(currentEntry.correctAnswers ?? [])[index]}
            isSubmitted={isSubmitted}
            isSingleChoice={isSingleChoice}
            revealUnselectedCorrect={revealUnselectedCorrect}
            onToggle={() => handleAnswerToggle(answer)}
          />
        ))}
      </div>
    </div>
  );

  const renderCodeVerdict = () => {
    if (!isSubmitted || isCorrect === null) return null;

    const feedback = isCorrect
      ? (currentEntry.userFeedback?.positive ?? "")
      : (currentEntry.userFeedback?.negative ?? "");

    return (
      <GradedFeedback
        result={{ correct: isCorrect, feedback }}
        className="w-full"
      />
    );
  };

  const renderFeedback = () => {
    if (isCodeVariant) return renderCodeVerdict();

    return (
      <QuizFeedback
        isSubmitted={isSubmitted}
        isCorrect={isCorrect}
        positiveFeedback={currentEntry.userFeedback?.positive}
        negativeFeedback={currentEntry.userFeedback?.negative}
        className="w-full"
      />
    );
  };

  const renderPrompt = () => {
    if (!isCodeVariant) {
      return (
        <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
          <div className="prose quiz-question max-w-full font-semibold text-primary">
            {question}
          </div>
        </QuizEntryCounter>
      );
    }

    return (
      <div className="shrink-0 space-y-4">
        <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
          <div className="prose quiz-question max-w-full font-semibold text-primary">
            {question}
          </div>
        </QuizEntryCounter>
        {renderCodeFences()}
      </div>
    );
  };

  const renderQuizBody = () => (
    <>
      {renderPrompt()}

      <div className="flex flex-1 flex-col items-center justify-start gap-4 overflow-y-auto pb-4 pt-8">
        {renderAnswerChoices()}
        {renderFeedback()}
      </div>

      <div className="shrink-0 pt-4">
        <div className="flex justify-center">
          <QuizCheckNextAction
            isSubmitted={isSubmitted}
            hasNextQuestion={currentIndex < entries.length - 1}
            canSubmit={selectedLabels.length > 0}
            onSubmit={handleSubmit}
            onNext={handleNext}
            submitLabel={
              collectsWithoutVerdict(revealFeedback) ? advanceLabel : undefined
            }
          />
        </div>
      </div>
    </>
  );

  return <div className="flex h-full flex-col">{renderQuizBody()}</div>;
};

export default QuizMultipleChoice;
